import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

export const useIntersectionObserver = (
  options: UseIntersectionObserverOptions = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const { threshold = 0.1, root = null, rootMargin = '50px', freezeOnceVisible = true } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // If we've already been visible and freezeOnceVisible is true, don't observe anymore
    if (hasBeenVisible && freezeOnceVisible) {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsIntersecting(isVisible);
        
        if (isVisible && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      },
      { threshold, root, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, root, rootMargin, hasBeenVisible, freezeOnceVisible]);

  return {
    elementRef,
    isIntersecting: hasBeenVisible && freezeOnceVisible ? true : isIntersecting,
    hasBeenVisible
  };
};